"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { PulsarService } from "@/services/pulsar.service";
import { useAuth } from "./useAuth";
import { MAINNET_CAMPAIGN_CONFIG, PORTAL_WALLET_LINK_CONFIG } from "@/config/campaign";

export const MAINNET_WALLET_LINK_KEYS = {
  group: (address?: string) =>
    ["mainnet", "wallet-links", PORTAL_WALLET_LINK_CONFIG.id, PORTAL_WALLET_LINK_CONFIG.groupId, address] as const,
};

export function useMainnetWalletLinks() {
  const { address, isAuthenticated, getApiToken } = useAuth();

  return useQuery({
    queryKey: MAINNET_WALLET_LINK_KEYS.group(address),
    queryFn: async () => {
      const token = await getApiToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.getMainnetLinkedWallets(token);
    },
    enabled: isAuthenticated && Boolean(address),
    staleTime: 30_000,
  });
}

export function useLinkMainnetWallet() {
  const queryClient = useQueryClient();
  const { address, getApiToken } = useAuth();

  return useMutation({
    mutationFn: async (payload: Parameters<typeof PulsarService.linkMainnetWallet>[0]) => {
      const token = await getApiToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.linkMainnetWallet(payload, token);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MAINNET_WALLET_LINK_KEYS.group(address) }),
        queryClient.invalidateQueries({
          queryKey: ["mainnet", "campaigns", MAINNET_CAMPAIGN_CONFIG.id, MAINNET_CAMPAIGN_CONFIG.groupId],
        }),
        queryClient.invalidateQueries({ queryKey: ["mainnet", "dashboard"] }),
      ]);
      toast.success("Wallet linked.");
    },
    onError: (error) => {
      toast.error(isAxiosError(error) && error.response?.status === 409
        ? "These wallets belong to different Portal accounts. Sign in to the Portal account you previously linked, or contact support."
        : isAxiosError(error) && error.response?.status === 401
          ? "Your session expired. Sign in again, then retry linking."
          : "Could not link wallet. Please retry; your existing links are unchanged.");
    },
  });
}

export function useSwitchMainnetPrimaryWallet() {
  const queryClient = useQueryClient();
  const { address, getApiToken } = useAuth();

  return useMutation({
    mutationFn: async (walletAddress: string) => {
      const token = await getApiToken();
      if (!token) {
        throw new Error("Authentication is still loading.");
      }

      return PulsarService.switchMainnetPrimaryWallet(walletAddress, token);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MAINNET_WALLET_LINK_KEYS.group(address) }),
        queryClient.invalidateQueries({
          queryKey: ["mainnet", "campaigns", MAINNET_CAMPAIGN_CONFIG.id, MAINNET_CAMPAIGN_CONFIG.groupId],
        }),
        queryClient.invalidateQueries({ queryKey: ["mainnet", "dashboard"] }),
      ]);
      toast.success("Primary wallet updated.");
    },
    onError: () => {
      toast.error("Could not switch primary wallet.");
    },
  });
}
